export interface Clock {
  /** Returns the next timestamp */
  advance(): string;
  /**
   * Advances the clock & creates a new moment, which can have more
   * timestamps within it.  I.e:
   *
   *
   *   (calls to this clock)
   *     1  -->  2  ----------------------> 3 --> ...
   *              \
   *               \ 2.1 --> 2.2 -> ...
   *   (calls to momentClock)
   */
  createMoment(): { start: string; clock: Clock };
}

const COUNT_MAX = 36 ** 5 - 1;
const COUNT_LENGTH = COUNT_MAX.toString(36).length;

export const MIN_TIMESAMP = '\x00';
export const MAX_TIMESTAMP = 'zzzzZ';

/** Add a suffix to all timestamps, e.g. the user ID.  This ensures that timestamps are unique. */
export class SuffixedClock implements Clock {
  constructor(private clock: Clock, private suffix: string) {}

  advance() {
    return this.clock.advance() + this.suffix;
  }

  createMoment() {
    const { start, clock } = this.clock.createMoment();
    return {
      start: start + this.suffix,
      clock: new SuffixedClock(clock, this.suffix),
    };
  }
}

// Runs out 2038-12-24.  Imagine if this code is still running in 15 years!
const TIME_MAX = 36 ** 6 - 1;
const TIME_LENGTH = TIME_MAX.toString(36).length;

/** A clock that uses the current time. */
export class TimeClock implements Clock {
  constructor() {}

  private lastNow = 0;
  private secondCounter = 0;

  advance() {
    const now = +Date.now();
    if (now === this.lastNow) {
      this.secondCounter++;
    } else {
      this.lastNow = now;
      this.secondCounter = 0;
    }
    if (this.secondCounter > COUNT_MAX) {
      throw new Error(`TimeClock ran out of counter values within a second`);
    }
    return `${now.toString(36).padStart(TIME_LENGTH)}.${this.secondCounter
      .toString(36)
      .padStart(COUNT_LENGTH, '0')}`;
  }

  createMoment() {
    const prefix = this.advance() + '.';
    const clock = new CounterClock(prefix);
    return { start: clock.advance(), clock };
  }
}

export class CounterClock implements Clock {
  private counter = 0;

  constructor(private prefix: string = '') {}

  advance() {
    const count = this.counter;
    this.counter++;
    if (this.counter > COUNT_MAX) {
      throw new Error(`CounterClock ran out of timestamps`);
    }
    return `${this.prefix}${count.toString(36).padStart(COUNT_LENGTH, '0')}`;
  }

  createMoment() {
    const prefix = this.advance() + '.';
    const clock = new CounterClock(prefix);
    return { start: clock.advance(), clock };
  }
}

export const STATIC_CLOCK: Clock = {
  advance() {
    return '01';
  },
  createMoment() {
    return { start: '01', clock: this };
  },
};
